// src/App.tsx
import React, { useEffect } from "react";
import ChatContainer from "./components/ChatContainer";
import ClientAnalyticsReportPage from "./pages/ClientAnalyticsReportPage";
import ConversationHistoryReportPage from "./pages/ConversationHistoryReportPage";
import { unlockAudio } from "./lib/sound"; // adjust if path differs
import AnalyticsOverviewReportPage from "./pages/AnalyticsOverviewReportPage";
import ClinicianPortalApp from "./features/clinicianPortal/ClinicianPortalApp";
import {
  CLINICIAN_PORTAL_NAVIGATION_EVENT,
  isClinicianPortalPath,
} from "./features/clinicianPortal/routing/portalRoutes";

export function normalizePathname(): string {
  let p = window.location.pathname || "/";
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

function useCurrentPathname(): string {
  const [location, setLocation] = React.useState(() => ({
    pathname: normalizePathname(),
    search: window.location.search,
  }));

  useEffect(() => {
    const updatePathname = () =>
      setLocation({
        pathname: normalizePathname(),
        search: window.location.search,
      });
    window.addEventListener("popstate", updatePathname);
    window.addEventListener(CLINICIAN_PORTAL_NAVIGATION_EVENT, updatePathname);

    return () => {
      window.removeEventListener("popstate", updatePathname);
      window.removeEventListener(CLINICIAN_PORTAL_NAVIGATION_EVENT, updatePathname);
    };
  }, []);

  return location.pathname;
}

function ChatApp() {
  console.log("✅ App rendering ChatContainer");

  useEffect(() => {
    const onFirstGesture = async () => {
      try {
        await unlockAudio();
        console.log("🔊 Audio unlocked in App.tsx");
      } catch (e) {
        console.warn("Audio unlock failed", e);
      }
    };
    // Listen for any real user gesture ONCE
    window.addEventListener("pointerdown", onFirstGesture, { once: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });

    return () => {
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
    };
  }, []);

  return <ChatContainer />;
}

export default function App() {
  const path = useCurrentPathname();

  if (path === "/reports/client") {
    return <ClientAnalyticsReportPage />;
  }

  if (path === "/reports/overview") {
    return <AnalyticsOverviewReportPage />;
  }

  if (path === "/reports/conversation") {
    return <ConversationHistoryReportPage />;
  }

  if (isClinicianPortalPath(path)) {
    return <ClinicianPortalApp />;
  }

  return <ChatApp />;
}
